/**
 * Save orchestration for an item edit session (see itemEditSession.js).
 *
 * Turns the session's save plan into API calls, sequentially, aborting on the
 * first error. Staged state is NOT cleared here — on failure the caller shows
 * the error and refetches session.affectedLaneIds() to reconcile with whatever
 * partially landed; on success it calls session.markSaved() and/or refetches.
 *
 * Order of operations:
 * 1. POST each staged copy (builds the tempId → saved-item map)
 * 2. POST bulk-restore — before updates, because bulk-update resolves items
 *    through the active-only queryset, so edits staged on a restored item
 *    must land after the restore
 * 3. PATCH bulk-update with field edits + cross-project moves (project_id)
 * 4. DELETE bulk-delete — after updates, so nothing edits a deleted row
 * 5. PATCH each affected project's item_sort, temp ids resolved to real ids
 *
 * Options (all optional; defaults preserve the projects2 behavior):
 * - buildCreatePayload(item, projectId): POST body for a staged copy
 * - buildUpdatePayload(item, update): bulk-update entry built from the full
 *   present item; default sends the diffed fields ({id, ...fields})
 * - expandUpdates(updates, session): adjust WHICH items get updated (e.g. the
 *   design studio's persist-all-wall-offsets policy adds {id}-only entries)
 * - createParams / updateParams: query params for the POST / bulk-update
 * - onCreated(tempId, savedItem): called after each successful create, so the
 *   caller can adopt the row mid-save (session.adoptCreated) and a failure in
 *   a later step can't re-create it on retry
 */

// The POST body for creating a design-studio item from an existing one.
export function cloneItemPayload(item, projectId, workspace = undefined) {
  return {
    imagedata: item.imagedata,
    config_data: item.config_data,
    workspace: workspace !== undefined ? workspace : (item.workspace || ''),
    project_id: Number(projectId),
    quantity: item.quantity,
    location: item.location,
    note: item.note,
    thumbnail: item.thumbnail || undefined,
    width: item.width,
    height: item.height,
  }
}

function withoutChosenOptionExtraData(payload) {
  const chosenOptions = payload?.config_data?.chosen_options
  if (!Array.isArray(chosenOptions)) return payload
  return {
    ...payload,
    config_data: {
      ...payload.config_data,
      chosen_options: chosenOptions.map(({ extra_data, ...option }) => option),
    },
  }
}

export class BulkItemSaveError extends Error {
  constructor(rowErrors, responseData = {}) {
    const first = rowErrors?.[0] || {}
    const serializerErrors = first.errors
    const message = first.error
      || (serializerErrors ? JSON.stringify(serializerErrors) : 'One or more items are invalid')
    super(message)
    this.name = 'BulkItemSaveError'
    this.rowErrors = rowErrors || []
    this.data = responseData
    // Keep the same shape as a single-item Axios validation failure. Existing
    // consumers (including Design Studio's field highlighter) can parse it,
    // while multi-item consumers can use rowErrors for every affected item.
    this.response = { data: serializerErrors || { detail: message } }
  }
}

export function itemValidationPayload(item) {
  if (!item?.id) return null
  return {
    id: item.id,
    imagedata: item.imagedata,
    substrate: item.substrate,
    action: item.action,
    workspace: item.workspace,
    quantity: item.quantity,
    location: item.location,
    note: item.note,
    width: item.width,
    height: item.height,
    config_data: item.config_data,
  }
}

export async function bulkUpdateItems(http, items, { dryRun = false, params = undefined } = {}) {
  const requestParams = { ...(params || {}), ...(dryRun ? { dry_run: true } : {}) }
  const config = Object.keys(requestParams).length ? { params: requestParams } : undefined
  return http.patch(
    '/api/v3/design-studio/items/bulk-update/',
    { items: items.map(withoutChosenOptionExtraData) },
    config,
  )
}

export function validateItemsForBulkSave(http, items, options = {}) {
  const payloads = items.map(itemValidationPayload).filter(Boolean)
  if (!payloads.length) {
    return Promise.resolve({ data: { updated: 0, valid: 0, errors: [], items: [], dry_run: true } })
  }
  return bulkUpdateItems(http, payloads, { ...options, dryRun: true })
}

export async function saveItemEditSession(session, {
  http,
  buildCreatePayload = cloneItemPayload,
  buildUpdatePayload = null,
  expandUpdates = null,
  createParams = undefined,
  updateParams = undefined,
  onCreated = null,
}) {
  const plan = session.buildSavePlan()

  // 1. Creates — tempToReal maps temp id → saved item (for markSaved and sort resolution)
  const tempToReal = {}
  for (const { tempId, projectId, item } of plan.creates) {
    const res = await http.post(
      '/api/v3/design-studio/items/',
      withoutChosenOptionExtraData(buildCreatePayload(item, projectId)),
      createParams ? { params: createParams } : undefined,
    )
    tempToReal[tempId] = res.data
    if (onCreated) onCreated(tempId, res.data)
  }

  // 2. Restores
  if (plan.restores.length) {
    await http.post('/api/v3/design-studio/items/bulk-restore/', { ids: plan.restores })
  }

  // 3. Field edits + moves
  const updates = expandUpdates ? expandUpdates(plan.updates, session) : plan.updates
  let updatedItems = []
  if (updates.length) {
    const items = buildUpdatePayload
      ? updates.map(update => buildUpdatePayload(session.getItem(update.id), update)).filter(Boolean)
      : updates
    const res = await bulkUpdateItems(http, items, { params: updateParams })
    const errors = res.data?.errors
    if (errors && errors.length) {
      throw new BulkItemSaveError(errors, res.data)
    }
    updatedItems = res.data?.items ?? []
  }

  // 4. Deletes
  if (plan.deletes.length) {
    await http.delete('/api/v3/design-studio/items/bulk-delete/', { data: { ids: plan.deletes } })
  }

  // 5. Sort orders. Non-numeric lanes (the design studio's 'standalone'
  // scene) have no project to PATCH; unresolved temp ids never save.
  for (const { projectId, orderedIds } of plan.sortUpdates) {
    if (!(Number(projectId) > 0)) continue
    const resolved = orderedIds
      .map(id => tempToReal[id]?.id ?? id)
      .filter(id => !(typeof id === 'number' && id < 0))
    await http.patch(`/api/v3/projects/${projectId}/`, { item_sort: resolved })
  }

  return { plan, tempToReal, updatedItems }
}
